import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import classnames from 'classnames';

import Button, { EditButton } from '@components/Button';
import Searchbox from '@components/Searchbox';
import Table from '@components/Table';

import { fetchEntitySections } from '@actions/entitySection';

import { entitySectionPartialURL } from '../AdminApp';

import EntityForm from './EntityForm';

import './style.scss';

interface IEntityListingProps extends RouteComponentProps<any> {
  // from redux
  activeAgenciesListing: IActiveAgencyListResponse;
  entitySections: IConfigXerceEntitySectionResponse[];
  fetchEntitySections: TFetchEntitySections;
}

interface IEntityListingState {
  currentAgency: IActiveAgencyResponse;
  searchQuery: string;
  currentEntitySectionId: number;
  isSearchBoxOpen: boolean;
  isEntityFormOpen: boolean;
}

class EntityListing extends React.Component<IEntityListingProps, IEntityListingState> {
  constructor(props: IEntityListingProps) {
    super(props);

    this.state = {
      searchQuery: '',
      currentAgency: null,
      currentEntitySectionId: null,
      isEntityFormOpen: false,
      isSearchBoxOpen: false,
    };
  }

  componentDidMount() {
    document.addEventListener('click', this.closeSearchBox);

    if (this.props.activeAgenciesListing) {
      const currentAgency = this.getSelectedAgencyFromProps(this.props);
      const refreshEntitySections = () => currentAgency && this.props.fetchEntitySections(currentAgency.id);

      this.setState(
        {
          currentAgency,
        },
        refreshEntitySections
      );
    }
  }

  componentWillReceiveProps(nextProps: IEntityListingProps) {
    const hasActiveAgenciesChanged = nextProps.activeAgenciesListing !== this.props.activeAgenciesListing;
    const areActiveAgenciesPresent = !!nextProps.activeAgenciesListing;
    const hasPathChanged: boolean = this.props.location.pathname !== nextProps.location.pathname;
    const isEntitySectionPath: boolean = nextProps.location.pathname.includes(entitySectionPartialURL);
    const isCurrentAgencyPresentInState = !!this.state.currentAgency;

    if (
      !isCurrentAgencyPresentInState &&
      ((hasActiveAgenciesChanged && areActiveAgenciesPresent) ||
        (hasPathChanged && isEntitySectionPath && areActiveAgenciesPresent))
    ) {
      const currentAgency = this.getSelectedAgencyFromProps(nextProps);
      const refreshEntitySections = () => currentAgency && this.props.fetchEntitySections(currentAgency.id);

      this.setState(
        {
          currentAgency,
        },
        refreshEntitySections
      );
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.closeSearchBox);
  }

  getSelectedAgencyFromProps = (nextProps: IEntityListingProps): IActiveAgencyResponse => {
    const { selectedAgencyId } = nextProps.match.params;
    const { activeAgenciesListing } = nextProps;
    let selectedAgency = activeAgenciesListing.agencies[0];

    if (selectedAgencyId) {
      const searchAgencyResults = activeAgenciesListing.agencies.filter((agency) => agency.id === +selectedAgencyId);

      selectedAgency = searchAgencyResults.length > 0 ? searchAgencyResults[0] : null;
    }

    return selectedAgency;
  };

  getActiveEntitySectionRows = () => {
    const { entitySections } = this.props;

    return entitySections
      ? entitySections
          .filter((entitySection) => entitySection.isActive)
          .map((filteredEntitySection) => ({
            id: filteredEntitySection.id,
            label: filteredEntitySection.label,
            violationTypeLabel: filteredEntitySection.configXerceViolationType.label,
          }))
      : [];
  };

  getInactiveEntitySectionRows = () => {
    const { entitySections } = this.props;

    return entitySections
      ? entitySections
          .filter((entitySection) => !entitySection.isActive)
          .map((filteredEntitySection) => ({
            id: filteredEntitySection.id,
            label: filteredEntitySection.label,
            violationTypeLabel: filteredEntitySection.configXerceViolationType.label,
          }))
      : [];
  };

  closeSearchBox = (e?) => {
    this.setState((prevState) => ({
      isSearchBoxOpen: false,
    }));
  };

  onSearchboxClick = (e: React.SyntheticEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  };

  toggleSearchBox = (e: React.SyntheticEvent<HTMLElement>) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    this.setState((prevState) => ({
      isSearchBoxOpen: !prevState.isSearchBoxOpen,
    }));
  };

  toggleEntityForm = (e: React.SyntheticEvent<HTMLElement>, currentEntitySectionId: number) => {
    this.setState((prevState) => ({
      isEntityFormOpen: !prevState.isEntityFormOpen,
      currentEntitySectionId,
    }));
  };

  toggleEntityFormEmpty = (e: React.SyntheticEvent<HTMLElement>) => this.toggleEntityForm(e, null);

  onSearchQueryChange = (newSearchQuery: string) => {
    this.setState({
      searchQuery: newSearchQuery,
    });
  };

  onEntityFormSubmit = (e: React.SyntheticEvent<HTMLElement>) => {
    this.setState({ isEntityFormOpen: false, currentEntitySectionId: null }, () =>
      this.props.fetchEntitySections(this.state.currentAgency.id)
    );
  };

  getSearchSuggestions = (): ISearchOptions[] => {
    const searchSuggestions: ISearchOptions[] = [];
    const { activeAgenciesListing } = this.props;
    const { searchQuery, currentAgency } = this.state;
    const currentAgencyId = currentAgency ? currentAgency.id : null;
    const activeAgencies = activeAgenciesListing ? activeAgenciesListing.agencies : [];
    const activeAgenciesSearchItems = activeAgencies.map((activeAgency) => ({
      name: activeAgency.name,
      value: activeAgency,
    }));
    const filteredSearchItems = activeAgenciesSearchItems.filter(
      (activeAgencyItem) =>
        activeAgencyItem.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        activeAgencyItem.value.id !== currentAgencyId
    );
    const items = filteredSearchItems;
    searchSuggestions.push({
      items,
    });

    return searchSuggestions;
  };

  onSuggestionSelect = (query: string, updatedAgency: IActiveAgencyResponse) => {
    this.setState(
      {
        currentAgency: updatedAgency,
        searchQuery: '',
        isSearchBoxOpen: false,
      },
      () => this.props.fetchEntitySections(updatedAgency.id)
    );
  };

  getColumns = () => [
    { accessor: 'label', Header: 'Label' },
    { accessor: 'violationTypeLabel', Header: 'Violation Type' },
    {
      accessor: 'id',
      Header: '',
      width: 50,
      Cell: (cell) => <EditButton onClick={(e) => this.toggleEntityForm(e, cell.value)} />,
      sortable: false,
    },
  ];

  renderSearchBox = () => {
    const searchSuggestions = this.getSearchSuggestions();

    return (
      <div
        className={classnames('entity-listing__search', {
          'entity-listing__search--open': this.state.isSearchBoxOpen,
        })}
        onClick={this.onSearchboxClick}
      >
        <Searchbox
          placeholder="Find an agency"
          query={this.state.searchQuery}
          onQueryChange={this.onSearchQueryChange}
          multiSection={false}
          search={() => null}
          onSuggestionSelect={this.onSuggestionSelect}
          searchResults={searchSuggestions}
          clearSuggestions={() => null}
          alwaysRenderSuggestions={this.state.isSearchBoxOpen}
          onSuggestionsClearRequested={this.closeSearchBox}
        />
      </div>
    );
  };

  renderActionsBar = () => (
    <div className={classnames('entity-listing__actions')}>
      <h2>Entities of </h2>
      <span
        className={classnames('entity-listing__searchbox', {
          'entity-listing__searchbox--open': this.state.isSearchBoxOpen,
        })}
        onClick={this.toggleSearchBox}
      >
        {this.state.currentAgency ? this.state.currentAgency.name : 'No Agency Selected'}
      </span>
      {this.renderSearchBox()}
      <Button content="Create Entity" bsStyle={ButtonStyle.PRIMARY} onClick={this.toggleEntityFormEmpty} />
    </div>
  );

  renderEmptyEntities = () => (
    <h5 className={classnames('entity-listing__table--empty', 'light-text', 'flex-column--center')}>
      No Entities Listed, as no Violation Types have been activated or there are No entities associated to Activated
      Violation Types.
    </h5>
  );

  renderEntityTables = () => {
    const columns = this.getColumns();
    const activeEntityRows = this.getActiveEntitySectionRows();
    const inactiveEntityRows = this.getInactiveEntitySectionRows();
    const noEntitiesPresent = activeEntityRows.length === 0 && inactiveEntityRows.length === 0;

    return noEntitiesPresent
      ? this.renderEmptyEntities()
      : [
          <div key={1} className={classnames('entity-listing__table-title')}>
            <h2>{`${activeEntityRows.length} Active`}</h2>
            <span className={classnames('light-text')}>Entities</span>
          </div>,
          <Table
            key={2}
            columns={columns}
            rows={activeEntityRows}
            noDataText="No Active Entities Found"
            plainTable={true}
          />,
          <div key={3} className={classnames('entity-listing__table-title')}>
            <h2>{`${inactiveEntityRows.length} Inactive`}</h2>
            <span className={classnames('light-text')}>Entities</span>
          </div>,
          <Table
            key={4}
            columns={columns}
            rows={inactiveEntityRows}
            noDataText="No Inactive Entities Found"
            plainTable={true}
          />,
        ];
  };

  render() {
    return (
      <div className={classnames('entity-listing')}>
        {this.renderActionsBar()}
        {this.renderEntityTables()}
        <EntityForm
          show={this.state.isEntityFormOpen}
          onClose={this.toggleEntityFormEmpty}
          onSubmit={this.onEntityFormSubmit}
          entitySectionId={this.state.currentEntitySectionId}
          agencyId={this.state.currentAgency ? this.state.currentAgency.id : null}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  activeAgenciesListing: state.agency.activeAgenciesListing,
  entitySections: state.entitySection.entitySections,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchEntitySections: (agencyId) => dispatch(fetchEntitySections(agencyId)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EntityListing));
